<template>
  <div class="page-content">
    <breadcrumb-index 
      :items="breadcrumbs"
      icon="help-circle"
      :badge="totalCount ? `${totalCount} questions` : null"
      badge-variant="badge-primary"
    >
      <template #actions>
        <button v-if="canModify" @click="openCreateModal" class="btn btn-primary btn-sm" :disabled="!selectedSubject">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Question
        </button>
      </template>
    </breadcrumb-index>

    <!-- Cascading Filter Section -->
    <div class="filter-cascade-section">
      <div class="filter-cascade-header">
        <vue-feather type="filter" size="18" />
        <h4>Select Category to View Questions</h4>
      </div>
      <div class="filter-cascade-grid">
        <!-- Step 1: Select Exam -->
        <div class="filter-step" :class="{ active: true, completed: selectedExam }">
          <div class="step-number">1</div>
          <div class="step-content">
            <label class="step-label">Select Exam</label>
            <select v-model="selectedExam" class="filter-select" @change="onExamChange" :disabled="loadingExams">
              <option value="">{{ loadingExams ? 'Loading exams...' : 'Choose an exam...' }}</option>
              <option v-for="exam in exams" :key="exam.id" :value="exam.id">
                {{ exam.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Step 2: Select Subject -->
        <div class="filter-step" :class="{ active: selectedExam, completed: selectedSubject, disabled: !selectedExam }">
          <div class="step-number">2</div>
          <div class="step-content">
            <label class="step-label">Select Subject</label>
            <select v-model="selectedSubject" class="filter-select" :disabled="!selectedExam || loadingSubjects" @change="onSubjectChange">
              <option value="">{{ loadingSubjects ? 'Loading subjects...' : 'Choose a subject...' }}</option>
              <option v-for="subject in filteredSubjects" :key="subject.id" :value="subject.id">
                {{ subject.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Optional Step 3: Filter by Topic -->
        <div class="filter-step optional" :class="{ active: selectedSubject, completed: selectedTopic, disabled: !selectedSubject }">
          <div class="step-number">3</div>
          <div class="step-content">
            <label class="step-label">Filter by Topic <span class="optional-tag">(Optional)</span></label>
            <select v-model="selectedTopic" class="filter-select" :disabled="!selectedSubject || loadingTopics" @change="fetchQuestions">
              <option value="">{{ loadingTopics ? 'Loading topics...' : 'All Topics' }}</option>
              <option v-for="topic in filteredTopics" :key="topic.id" :value="topic.id">
                {{ topic.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Questions Table - Only show when subject is selected -->
    <div v-if="selectedSubject" class="questions-content">
      <DataTable
        :items="questions"
        :columns="columns"
        :loading="loading"
        :paginated="false"
        search-placeholder="Search questions..."
        empty-icon="help-circle"
        empty-title="No questions found"
        :empty-description="emptyDescription"
      >
        <template #filters>
          <select v-model="filterType" class="filter-select-modern" @change="fetchQuestions">
            <option value="">All Types</option>
            <option value="mcq">MCQ</option>
            <option value="numerical">Numerical</option>
            <option value="match">Match</option>
            <option value="subjective">Subjective</option>
          </select>
          <select v-model="filterDifficulty" class="filter-select-modern" @change="fetchQuestions">
            <option value="">All Difficulty</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
          <select v-model="filterLanguage" class="filter-select-modern" @change="fetchQuestions">
            <option value="">All Languages</option>
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
        </template>

        <template #cell-question="{ item }">
          <div class="question-preview-wrapper" v-tooltip="{ text: item.questionText || item.content || item.text, position: 'top', variant: 'light', html: true }">
            <KatexContent :content="(item.questionText || item.content || item.text || '').substring(0, 60) + ((item.questionText || item.content || item.text || '').length > 60 ? '...' : '')" />
          </div>
        </template>

        <template #cell-type="{ item }">
          <span :class="['badge', getTypeBadge(item.question_type || item.type)]">
            {{ formatType(item.question_type || item.type) }}
          </span>
        </template>

        <template #cell-difficulty="{ item }">
          <span :class="['badge', getDifficultyBadge(item.difficulty)]">{{ item.difficulty || '-' }}</span>
        </template>

        <template #cell-aiGenerated="{ item }">
          <span v-if="item.isAiGenerated" class="badge bg-purple" v-tooltip="'AI Generated'">
            <vue-feather type="cpu" size="12" />
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell-marks="{ item }">
          <span class="fw-medium">{{ item.marks || 1 }}</span>
        </template>

        <template #cell-actions="{ item }">
          <div class="action-buttons">
            <button class="action-btn action-view" @click="openViewModal(item)" v-tooltip="'View details'">
              <vue-feather type="eye" size="14" />
            </button>
            <button v-if="canModify" class="action-btn action-edit" @click="openEditModal(item)" v-tooltip="'Edit question'">
              <vue-feather type="edit-2" size="14" />
            </button>
            <button v-if="canModify" class="action-btn action-delete" @click="confirmDelete(item)" v-tooltip="{ text: 'Delete question', variant: 'danger' }">
              <vue-feather type="trash-2" size="14" />
            </button>
          </div>
        </template>

        <template #empty-action>
          <button @click="openCreateModal" class="btn btn-primary">
            <vue-feather type="plus" size="16" class="me-1" />
            Add First Question
          </button>
        </template>
      </DataTable>

      <!-- Server-Side Pagination -->
      <div v-if="totalCount > 0" class="server-pagination">
        <div class="pagination-info">
          Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ totalCount }}
        </div>
        <div class="pagination-controls">
          <button class="pagination-btn" :disabled="currentPage === 1" @click="goToPage(1)" title="First page">
            <vue-feather type="chevrons-left" size="16" />
          </button>
          <button class="pagination-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)" title="Previous page">
            <vue-feather type="chevron-left" size="16" />
          </button>
          <div class="pagination-pages">
            <button v-for="page in visiblePages" :key="page" class="pagination-page" :class="{ active: page === currentPage, ellipsis: page === '...' }" :disabled="page === '...'" @click="page !== '...' && goToPage(page)">
              {{ page }}
            </button>
          </div>
          <button class="pagination-btn" :disabled="!hasMore" @click="goToPage(currentPage + 1)" title="Next page">
            <vue-feather type="chevron-right" size="16" />
          </button>
          <button class="pagination-btn" :disabled="!hasMore" @click="goToPage(totalPages)" title="Last page">
            <vue-feather type="chevrons-right" size="16" />
          </button>
        </div>
        <div class="pagination-size">
          <select v-model="pageSize" class="page-size-select" @change="onPageSizeChange">
            <option v-for="size in [10, 25, 50, 100]" :key="size" :value="size">{{ size }} per page</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Empty State when no subject selected -->
    <div v-else class="select-prompt">
      <div class="select-prompt-icon">
        <vue-feather type="layers" size="64" />
      </div>
      <h3>Select an Exam and Subject</h3>
      <p>Choose an exam and subject from the filters above to view and manage questions.</p>
    </div>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Question' : 'Add Question'"
      icon="help-circle"
      size="lg"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.content || !form.question_type"
      :item-id="isEditing ? editingId : ''"
      @submit="saveQuestion"
    >
      <form @submit.prevent="saveQuestion" class="modal-form">
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Question Type <span class="text-danger">*</span></label>
            <select v-model="form.question_type" class="form-control" required>
              <option value="">Select Type</option>
              <option value="mcq">Multiple Choice (MCQ)</option>
              <option value="numerical">Numerical</option>
              <option value="match">Match</option>
              <option value="subjective">Subjective</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Difficulty</label>
            <select v-model="form.difficulty" class="form-control">
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        </div>

        <!-- Assignment Section -->
        <div class="assignment-section">
          <label class="section-label">
            <vue-feather type="link" size="14" />
            Assignment
          </label>
          <div class="assignment-info">
            <span class="assignment-tag"><vue-feather type="book-open" size="12" /> {{ selectedExamName }}</span>
            <span class="assignment-tag"><vue-feather type="layers" size="12" /> {{ selectedSubjectName }}</span>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Topic</label>
              <select v-model="form.topic_id" class="form-control">
                <option value="">Select Topic</option>
                <option v-for="topic in filteredTopics" :key="topic.id" :value="topic.id">{{ topic.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Lesson</label>
              <select v-model="form.lesson_id" class="form-control" :disabled="!formLessons.length && !loadingLessons">
                <option value="">{{ loadingLessons ? 'Loading lessons...' : 'Select Lesson' }}</option>
                <option v-for="lesson in formLessons" :key="lesson.id" :value="lesson.id">{{ lesson.title }}</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Test</label>
            <select v-model="form.test_id" class="form-control" :disabled="!formTests.length && !loadingTests">
              <option value="">{{ loadingTests ? 'Loading tests...' : 'Select Test' }}</option>
              <option v-for="test in formTests" :key="test.id" :value="test.id">{{ test.title }}</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Question <span class="text-danger">*</span></label>
          <textarea v-model="form.content" class="form-control" rows="3" placeholder="Enter your question..." required></textarea>
        </div>

        <!-- AI Enhance Button -->
        <div class="ai-enhance-section" v-if="form.content">
          <button type="button" @click="aiEnhanceQuestion" class="ai-enhance-btn" :disabled="aiEnhancing">
            <vue-feather :type="aiEnhancing ? 'loader' : 'cpu'" size="16" :class="{ 'spin': aiEnhancing }" />
            {{ aiEnhancing ? 'Enhancing...' : 'AI Enhance' }}
          </button>
          <button type="button" @click="aiTranslateQuestion" class="ai-translate-btn" :disabled="aiTranslating">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'AI Translate' }}
          </button>
          <span class="ai-hint">Auto-fill options, explanation & shortcut method</span>
        </div>

        <!-- Translation Language Selector -->
        <div class="ai-translate-section" v-if="form.content && showTranslateOptions">
          <label class="form-label">Translate to</label>
          <select v-model="translateTargetLang" class="form-control form-control-sm" style="max-width: 200px;">
            <option v-for="lang in languageOptions.filter(l => l.code !== form.language)" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <button type="button" @click="confirmTranslateQuestion" class="ai-translate-btn" :disabled="aiTranslating || !translateTargetLang">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'Translate Now' }}
          </button>
        </div>

        <!-- MCQ Options -->
        <div v-if="form.question_type === 'mcq'" class="options-section">
          <label class="form-label">Options</label>
          <div class="options-grid">
            <div class="option-input">
              <span class="option-label">A</span>
              <input v-model="form.option_a" type="text" class="form-control" placeholder="Option A" />
            </div>
            <div class="option-input">
              <span class="option-label">B</span>
              <input v-model="form.option_b" type="text" class="form-control" placeholder="Option B" />
            </div>
            <div class="option-input">
              <span class="option-label">C</span>
              <input v-model="form.option_c" type="text" class="form-control" placeholder="Option C" />
            </div>
            <div class="option-input">
              <span class="option-label">D</span>
              <input v-model="form.option_d" type="text" class="form-control" placeholder="Option D" />
            </div>
          </div>
          <div class="form-group mt-3">
            <label class="form-label">Correct Answer</label>
            <select v-model="form.correct_answer" class="form-control">
              <option value="">Select Correct Answer</option>
              <option value="a">Option A</option>
              <option value="b">Option B</option>
              <option value="c">Option C</option>
              <option value="d">Option D</option>
            </select>
          </div>
        </div>

        <!-- Non-MCQ Answer -->
        <div v-else-if="form.question_type" class="form-group">
          <label class="form-label">Answer</label>
          <textarea v-model="form.correct_answer" class="form-control" rows="2" placeholder="Enter the correct answer..."></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Explanation</label>
          <textarea v-model="form.explanation" class="form-control" rows="2" placeholder="Explain why this is the correct answer..."></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">
            <vue-feather type="zap" size="14" class="me-1" style="color: #f59e0b;" />
            Shortcut Method
          </label>
          <textarea v-model="form.shortcut_method" class="form-control" rows="2" placeholder="Quick trick or shortcut to solve this question..."></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">
            <vue-feather type="cpu" size="14" class="me-1" style="color: #10b981;" />
            Memory Hack
          </label>
          <textarea v-model="form.memory_hack" class="form-control" rows="2" placeholder="Mnemonic or tip to remember the concept..."></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">
            <vue-feather type="alert-triangle" size="14" class="me-1" style="color: #ef4444;" />
            Trap Analysis
          </label>
          <textarea v-model="form.trap_analysis" class="form-control" rows="2" placeholder="Why students might choose each wrong option..."></textarea>
        </div>

        <!-- Attachments Section -->
        <div class="form-group">
          <label class="form-label">
            <vue-feather type="paperclip" size="14" class="me-1" style="color: #6366f1;" />
            Attachments
          </label>
          <div class="attachment-input-row">
            <input
              v-model="newAttachmentUrl"
              type="url"
              class="form-control"
              placeholder="Paste image or file URL..."
              @keydown.enter.prevent="addAttachmentUrl"
            />
            <button type="button" class="btn btn-outline-primary btn-sm" @click="addAttachmentUrl" :disabled="!newAttachmentUrl.trim()">
              <vue-feather type="plus" size="14" />
              Add
            </button>
          </div>
          <small v-if="attachmentError" class="text-danger">{{ attachmentError }}</small>
          <div v-if="form.attachment_urls.length" class="attachment-list">
            <div v-for="(url, index) in form.attachment_urls" :key="index" class="attachment-item">
              <img v-if="isImageUrl(url)" :src="url" class="attachment-thumb" alt="attachment preview" />
              <vue-feather v-else type="file" size="16" class="attachment-file-icon" />
              <span class="attachment-name" :title="url">{{ getAttachmentFilename(url) }}</span>
              <button type="button" class="attachment-remove" @click="removeAttachmentUrl(index)" title="Remove">
                <vue-feather type="x" size="14" />
              </button>
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Marks</label>
            <input v-model.number="form.marks" type="number" class="form-control" placeholder="1" min="1" />
          </div>
          <div class="form-group">
            <label class="form-label">Language</label>
            <select v-model="form.language" class="form-control" @change="onLanguageChange">
              <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
            </select>
            <small v-if="loadingTranslation" class="text-info"><vue-feather type="loader" size="12" class="spin" /> Loading translation...</small>
          </div>
        </div>
      </form>
    </CreateModal>

    <!-- View Modal -->
    <QuestionViewModal
      v-model="showViewModal"
      :question="viewingQuestion"
      :on-edit="() => { showViewModal = false; openEditModal(viewingQuestion); }"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useAlert } from '@/plugins/sweetalert'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'
import KatexContent from '@/components/content/KatexContent.vue'
import QuestionViewModal from '@/components/modals/QuestionViewModal.vue'
import { languageOptions } from '@/utils/languageOptions'

export default {
  name: 'QuestionsList',
  components: { DataTable, CreateModal, KatexContent, QuestionViewModal },
  setup() {
    const authStore = useAuthStore()
    const alert = useAlert()
    const canModify = computed(() => authStore.isSuperAdmin || authStore.isAdmin || authStore.isContentManager)
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Questions', active: true }
    ]

    const columns = [
      { key: 'question', label: 'Question', sortable: true, minWidth: '300px' },
      { key: 'type', label: 'Type', sortable: true },
      { key: 'difficulty', label: 'Difficulty', sortable: true },
      { key: 'aiGenerated', label: 'AI', sortable: true },
      { key: 'marks', label: 'Marks', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    // Data
    const questions = ref([])
    const loading = ref(false)
    const totalCount = ref(0)
    const currentPage = ref(1)
    const pageSize = ref(10)
    const hasMore = ref(false)

    // Cascading filter state
    const exams = ref([])
    const subjects = ref([])
    const topics = ref([])
    const lessons = ref([])
    const tests = ref([])
    const selectedExam = ref('')
    const selectedSubject = ref('')
    const selectedTopic = ref('')
    const filterType = ref('')
    const filterDifficulty = ref('')
    const filterLanguage = ref('')

    // Modal state
    const showModal = ref(false)
    const showViewModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const viewingQuestion = ref(null)
    const aiEnhancing = ref(false)
    const aiTranslating = ref(false)
    const showTranslateOptions = ref(false)
    const translateTargetLang = ref('hi')
    const loadingTranslation = ref(false)

    const form = reactive({
      topic_id: '',
      lesson_id: '',
      test_id: '',
      question_type: 'mcq',
      content: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: '',
      correct_answer: '',
      explanation: '',
      shortcut_method: '',
      memory_hack: '',
      trap_analysis: '',
      difficulty: 'medium',
      marks: 1,
      language: 'en',
      attachment_urls: []
    })

    // Attachment URL management
    const newAttachmentUrl = ref('')
    const attachmentError = ref('')

    const addAttachmentUrl = () => {
      const url = newAttachmentUrl.value.trim()
      if (!url) return
      try {
        new URL(url)
      } catch {
        attachmentError.value = 'Please enter a valid URL'
        return
      }
      if (form.attachment_urls.includes(url)) {
        attachmentError.value = 'This URL is already added'
        return
      }
      attachmentError.value = ''
      form.attachment_urls.push(url)
      newAttachmentUrl.value = ''
    }

    const removeAttachmentUrl = (index) => {
      form.attachment_urls.splice(index, 1)
    }

    const getAttachmentFilename = (url) => {
      try {
        const pathname = new URL(url).pathname
        return pathname.split('/').pop() || url
      } catch {
        return url
      }
    }

    const isImageUrl = (url) => {
      return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(url)
    }

    // Computed — data is already scoped from API, no client-side filtering needed
    const filteredSubjects = computed(() => subjects.value)

    const filteredTopics = computed(() => topics.value)

    const formLessons = computed(() => lessons.value)

    const formTests = computed(() => tests.value)

    const selectedExamName = computed(() => {
      const exam = exams.value.find(e => e.id === selectedExam.value)
      return exam?.name || 'Not selected'
    })

    const selectedSubjectName = computed(() => {
      const subject = subjects.value.find(s => s.id === selectedSubject.value)
      return subject?.name || 'Not selected'
    })

    const emptyDescription = computed(() => {
      if (selectedTopic.value) return 'No questions found for this topic. Add your first question.'
      return 'No questions found for this subject. Add your first question or select a specific topic.'
    })

    // Pagination computed
    const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))
    const startIndex = computed(() => (currentPage.value - 1) * pageSize.value)
    const endIndex = computed(() => Math.min(startIndex.value + pageSize.value, totalCount.value))
    
    const visiblePages = computed(() => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        if (current > 3) pages.push('...')
        const start = Math.max(2, current - 1)
        const end = Math.min(total - 1, current + 1)
        for (let i = start; i <= end; i++) pages.push(i)
        if (current < total - 2) pages.push('...')
        pages.push(total)
      }
      return pages
    })

    // Loading states for cascading dropdowns
    const loadingExams = ref(false)
    const loadingSubjects = ref(false)
    const loadingTopics = ref(false)
    const loadingLessons = ref(false)
    const loadingTests = ref(false)

    // Fetch only exams on mount — subjects/topics/lessons/tests load on demand
    const fetchInitialData = async () => {
      loadingExams.value = true
      try {
        const examsRes = await api.get('/v1/admin/exams')
        exams.value = examsRes.data.exams || []
      } catch (err) {
        console.error('Failed to fetch exams:', err)
      } finally {
        loadingExams.value = false
      }
    }

    // Load subjects for the selected exam
    const fetchSubjectsForExam = async (examId) => {
      if (!examId) { subjects.value = []; return }
      loadingSubjects.value = true
      try {
        const res = await api.get(`/v1/admin/exams/${examId}/subjects`)
        subjects.value = res.data.subjects || []
      } catch (err) {
        console.error('Failed to fetch subjects:', err)
        subjects.value = []
      } finally {
        loadingSubjects.value = false
      }
    }

    // Load topics for the selected subject
    const fetchTopicsForSubject = async (subjectId) => {
      if (!subjectId) { topics.value = []; return }
      loadingTopics.value = true
      try {
        const res = await api.get(`/v1/admin/subjects/${subjectId}/topics`)
        topics.value = res.data.topics || []
      } catch (err) {
        console.error('Failed to fetch topics:', err)
        topics.value = []
      } finally {
        loadingTopics.value = false
      }
    }

    // Load lessons for a topic (used in form modal)
    const fetchLessonsForTopic = async (topicId) => {
      if (!topicId) { lessons.value = []; return }
      loadingLessons.value = true
      try {
        const res = await api.get(`/v1/admin/topics/${topicId}/lessons`)
        lessons.value = res.data.lessons || []
      } catch (err) {
        console.error('Failed to fetch lessons:', err)
        lessons.value = []
      } finally {
        loadingLessons.value = false
      }
    }

    // Load tests for a lesson (used in form modal)
    const fetchTestsForLesson = async (lessonId) => {
      if (!lessonId) { tests.value = []; return }
      loadingTests.value = true
      try {
        const res = await api.get(`/v1/admin/lessons/${lessonId}/tests`)
        tests.value = res.data.tests || []
      } catch (err) {
        console.error('Failed to fetch tests:', err)
        tests.value = []
      } finally {
        loadingTests.value = false
      }
    }

    // Fetch questions with filters and pagination
    const fetchQuestions = async () => {
      if (!selectedSubject.value) {
        questions.value = []
        totalCount.value = 0
        hasMore.value = false
        return
      }

      loading.value = true
      try {
        const params = new URLSearchParams()
        params.append('subjectId', selectedSubject.value)
        if (selectedTopic.value) params.append('topicId', selectedTopic.value)
        if (filterType.value) params.append('question_type', filterType.value)
        if (filterDifficulty.value) params.append('difficulty', filterDifficulty.value)
        if (filterLanguage.value) params.append('language', filterLanguage.value)
        params.append('offset', (currentPage.value - 1) * pageSize.value)
        params.append('limit', pageSize.value)

        const response = await api.get(`/v1/admin/questions?${params.toString()}`)
        questions.value = response.data.questions || []
        totalCount.value = response.data.total || 0
        hasMore.value = response.data.hasMore || false
      } catch (err) {
        console.error('Failed to fetch questions:', err)
        questions.value = []
        totalCount.value = 0
        hasMore.value = false
      } finally {
        loading.value = false
      }
    }

    // Event handlers — cascade API calls on selection change
    const onExamChange = async () => {
      selectedSubject.value = ''
      selectedTopic.value = ''
      subjects.value = []
      topics.value = []
      lessons.value = []
      tests.value = []
      questions.value = []
      totalCount.value = 0
      await fetchSubjectsForExam(selectedExam.value)
    }

    const onSubjectChange = async () => {
      selectedTopic.value = ''
      topics.value = []
      lessons.value = []
      tests.value = []
      currentPage.value = 1
      await fetchTopicsForSubject(selectedSubject.value)
      fetchQuestions()
    }

    const goToPage = (page) => {
      currentPage.value = page
      fetchQuestions()
    }

    const onPageSizeChange = () => {
      currentPage.value = 1
      fetchQuestions()
    }

    // Form topic watcher — fetch lessons on demand when topic changes in form
    watch(() => form.topic_id, (newTopicId) => {
      form.lesson_id = ''
      form.test_id = ''
      fetchLessonsForTopic(newTopicId)
    })

    // Form lesson watcher — fetch tests on demand when lesson changes in form
    watch(() => form.lesson_id, (newLessonId) => {
      form.test_id = ''
      fetchTestsForLesson(newLessonId)
    })

    // Modal functions
    const resetForm = () => {
      form.topic_id = ''
      form.lesson_id = ''
      form.test_id = ''
      form.question_type = 'mcq'
      form.content = ''
      form.option_a = ''
      form.option_b = ''
      form.option_c = ''
      form.option_d = ''
      form.correct_answer = ''
      form.explanation = ''
      form.shortcut_method = ''
      form.memory_hack = ''
      form.trap_analysis = ''
      form.difficulty = 'medium'
      form.marks = 1
      form.language = 'en'
      form.attachment_urls = []
      newAttachmentUrl.value = ''
      attachmentError.value = ''
      editingId.value = null
      isEditing.value = false
    }

    const openCreateModal = () => {
      if (!selectedSubject.value) return
      resetForm()
      showModal.value = true
    }

    const openViewModal = (question) => {
      viewingQuestion.value = question
      showViewModal.value = true
    }

    const openEditModal = (question) => {
      isEditing.value = true
      editingId.value = question.id
      form.question_type = question.question_type || question.type || 'mcq'
      form.content = question.questionText || question.content || question.text || ''
      form.difficulty = question.difficulty || 'medium'
      form.language = question.language || 'en'
      form.topic_id = question.topic_id || question.topicId || ''
      form.lesson_id = question.lesson_id || question.lessonId || ''
      form.test_id = question.test_id || question.testId || ''

      let answerMeta = {}
      if (question.answerMeta) {
        try {
          answerMeta = typeof question.answerMeta === 'string' ? JSON.parse(question.answerMeta) : question.answerMeta
        } catch (e) { console.log('Failed to parse answerMeta:', e) }
      }
      form.marks = question.marks || answerMeta.marks || 1
      form.explanation = question.explanation || answerMeta.explanation || ''
      form.shortcut_method = question.shortcut_method || question.shortcutMethod || answerMeta.shortcut_method || ''
      form.memory_hack = question.memory_hack || question.memoryHack || answerMeta.memory_hack || ''
      form.trap_analysis = question.trap_analysis || question.trapAnalysis || answerMeta.trap_analysis || ''
      form.correct_answer = question.correct_answer || question.correctAnswer || answerMeta.correct_answer || ''

      if (form.question_type === 'mcq') {
        let opts = null
        if (question.options) {
          try { opts = typeof question.options === 'string' ? JSON.parse(question.options) : question.options } catch (e) {}
        }
        if (opts) {
          form.option_a = opts.a || opts.A || ''
          form.option_b = opts.b || opts.B || ''
          form.option_c = opts.c || opts.C || ''
          form.option_d = opts.d || opts.D || ''
        }
      }

      // Populate attachment URLs
      const attachments = question.attachmentUrls || question.attachment_urls || []
      form.attachment_urls = Array.isArray(attachments) ? [...attachments] : []

      showModal.value = true
    }

    const saveQuestion = async () => {
      if (!form.content || !form.question_type) return
      saving.value = true
      try {
        const payload = {
          exam_id: selectedExam.value,
          subject_id: selectedSubject.value,
          topic_id: form.topic_id || null,
          lesson_id: form.lesson_id || null,
          test_id: form.test_id || null,
          question_type: form.question_type,
          content: form.content,
          difficulty: form.difficulty,
          marks: form.marks,
          explanation: form.explanation,
          shortcut_method: form.shortcut_method,
          memory_hack: form.memory_hack,
          trap_analysis: form.trap_analysis,
          language_code: form.language
        }

        if (form.attachment_urls.length > 0) {
          payload.attachment_urls = form.attachment_urls
        }

        if (form.question_type === 'mcq') {
          payload.options = { a: form.option_a, b: form.option_b, c: form.option_c, d: form.option_d }
        }
        payload.correct_answer = form.correct_answer

        if (isEditing.value) {
          await api.put(`/v1/admin/questions/${editingId.value}`, payload)
          alert.success('Updated!', 'Question updated successfully')
        } else {
          await api.post('/v1/admin/questions', payload)
          alert.success('Created!', 'Question created successfully')
        }
        showModal.value = false
        resetForm()
        fetchQuestions()
      } catch (error) {
        alert.error('Error', error.response?.data?.error?.message || 'Failed to save question')
      } finally {
        saving.value = false
      }
    }

    const confirmDelete = async (question) => {
      const result = await alert.delete('Delete Question?', 'Are you sure you want to delete this question?')
      if (result.isConfirmed) {
        try {
          await api.delete(`/v1/admin/questions/${question.id}`)
          alert.success('Deleted!', 'Question has been deleted.')
          fetchQuestions()
        } catch (err) {
          alert.error('Error', 'Failed to delete question')
        }
      }
    }

    // AI Enhancement
    const parseAIResponse = (response) => {
      if (response.data?.structured_data?.data) return response.data.structured_data.data
      const responseText = response.data?.response || response.data?.message || ''

      const normalizeParsed = (parsed) => {
        if (parsed.questions && Array.isArray(parsed.questions) && parsed.questions.length > 0) {
          const q = parsed.questions[0]
          return {
            content: q.content || '',
            option_a: q.options?.a || q.options?.A || q.option_a || '',
            option_b: q.options?.b || q.options?.B || q.option_b || '',
            option_c: q.options?.c || q.options?.C || q.option_c || '',
            option_d: q.options?.d || q.options?.D || q.option_d || '',
            correct_answer: q.correct_answer || '',
            explanation: q.explanation || '',
            shortcut_method: q.shortcut_method || '',
            memory_hack: q.memory_hack || '',
            trap_analysis: q.trap_analysis || '',
            difficulty: q.difficulty || 'medium'
          }
        }
        if (parsed.options) {
          return {
            content: parsed.content || '',
            option_a: parsed.options?.a || parsed.options?.A || '',
            option_b: parsed.options?.b || parsed.options?.B || '',
            option_c: parsed.options?.c || parsed.options?.C || '',
            option_d: parsed.options?.d || parsed.options?.D || '',
            correct_answer: parsed.correct_answer || '',
            explanation: parsed.explanation || '',
            shortcut_method: parsed.shortcut_method || '',
            memory_hack: parsed.memory_hack || '',
            trap_analysis: parsed.trap_analysis || '',
            difficulty: parsed.difficulty || 'medium'
          }
        }
        return parsed
      }

      // Try extracting JSON from markdown code fences
      const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/)
      if (jsonMatch) {
        try {
          return normalizeParsed(JSON.parse(jsonMatch[1].trim()))
        } catch (e) { console.log('Failed to parse fenced JSON:', e) }
      }

      // Try parsing the response text directly as JSON
      const trimmed = responseText.trim()
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          return normalizeParsed(JSON.parse(trimmed))
        } catch (e) { console.log('Failed to parse raw JSON:', e) }
      }

      return {}
    }

    const aiEnhanceQuestion = async () => {
      if (!form.content) {
        alert.error('Error', 'Please enter a question first')
        return
      }
      aiEnhancing.value = true
      try {
        const response = await api.post('/v1/admin/admin-chat', {
          message: `Enhance this ${form.question_type.toUpperCase()} question for an educational test.\n\nQuestion: ${form.content}\n\nPlease provide your response as a valid JSON object with these exact keys:\n{\n  "option_a": "First option text",\n  "option_b": "Second option text",\n  "option_c": "Third option text",\n  "option_d": "Fourth option text",\n  "correct_answer": "a",\n  "explanation": "Detailed explanation",\n  "shortcut_method": "Quick trick",\n  "memory_hack": "Mnemonic or tip to remember the concept",\n  "trap_analysis": "Why students might choose each wrong option",\n  "difficulty": "medium"\n}`,
          generation_type: 'question_enhance',
          output_format: 'structured'
        })
        const aiData = parseAIResponse(response)
        let fieldsUpdated = 0
        if (form.question_type === 'mcq') {
          if (aiData.option_a) { form.option_a = aiData.option_a; fieldsUpdated++ }
          if (aiData.option_b) { form.option_b = aiData.option_b; fieldsUpdated++ }
          if (aiData.option_c) { form.option_c = aiData.option_c; fieldsUpdated++ }
          if (aiData.option_d) { form.option_d = aiData.option_d; fieldsUpdated++ }
          if (aiData.correct_answer) { form.correct_answer = String(aiData.correct_answer).toLowerCase().charAt(0); fieldsUpdated++ }
        }
        if (aiData.explanation) { form.explanation = aiData.explanation; fieldsUpdated++ }
        if (aiData.shortcut_method) { form.shortcut_method = aiData.shortcut_method; fieldsUpdated++ }
        if (aiData.memory_hack) { form.memory_hack = aiData.memory_hack; fieldsUpdated++ }
        if (aiData.trap_analysis) { form.trap_analysis = aiData.trap_analysis; fieldsUpdated++ }
        if (aiData.difficulty && ['easy', 'medium', 'hard'].includes(String(aiData.difficulty).toLowerCase())) {
          form.difficulty = String(aiData.difficulty).toLowerCase(); fieldsUpdated++
        }
        if (fieldsUpdated > 0) alert.success('Enhanced!', `AI has filled in ${fieldsUpdated} field(s)`)
        else alert.warning('Partial Success', 'AI responded but could not extract structured data.')
      } catch (error) {
        console.error('AI enhancement failed:', error)
        alert.error('Error', 'Failed to enhance question with AI.')
      } finally {
        aiEnhancing.value = false
      }
    }

    // AI Translation
    const aiTranslateQuestion = () => {
      if (!form.content) {
        alert.error('Error', 'Please enter a question first')
        return
      }
      showTranslateOptions.value = !showTranslateOptions.value
    }

    const confirmTranslateQuestion = async () => {
      if (!form.content || !translateTargetLang.value) return
      const targetLangLabel = languageOptions.find(l => l.code === translateTargetLang.value)?.label || translateTargetLang.value
      aiTranslating.value = true
      try {
        const fieldsToTranslate = { content: form.content }
        if (form.option_a) fieldsToTranslate.option_a = form.option_a
        if (form.option_b) fieldsToTranslate.option_b = form.option_b
        if (form.option_c) fieldsToTranslate.option_c = form.option_c
        if (form.option_d) fieldsToTranslate.option_d = form.option_d
        if (form.explanation) fieldsToTranslate.explanation = form.explanation
        if (form.shortcut_method) fieldsToTranslate.shortcut_method = form.shortcut_method
        if (form.memory_hack) fieldsToTranslate.memory_hack = form.memory_hack
        if (form.trap_analysis) fieldsToTranslate.trap_analysis = form.trap_analysis

        const response = await api.post('/v1/admin/admin-chat', {
          message: `Translate the following educational question content to ${targetLangLabel}. Keep mathematical formulas, LaTeX expressions, and technical terms intact. Return ONLY a valid JSON object with the same keys translated:\n\n${JSON.stringify(fieldsToTranslate, null, 2)}`,
          generation_type: 'translate',
          output_format: 'structured'
        })
        const aiData = parseAIResponse(response)
        let fieldsUpdated = 0
        if (aiData.content) { form.content = aiData.content; fieldsUpdated++ }
        if (aiData.option_a) { form.option_a = aiData.option_a; fieldsUpdated++ }
        if (aiData.option_b) { form.option_b = aiData.option_b; fieldsUpdated++ }
        if (aiData.option_c) { form.option_c = aiData.option_c; fieldsUpdated++ }
        if (aiData.option_d) { form.option_d = aiData.option_d; fieldsUpdated++ }
        if (aiData.explanation) { form.explanation = aiData.explanation; fieldsUpdated++ }
        if (aiData.shortcut_method) { form.shortcut_method = aiData.shortcut_method; fieldsUpdated++ }
        if (aiData.memory_hack) { form.memory_hack = aiData.memory_hack; fieldsUpdated++ }
        if (aiData.trap_analysis) { form.trap_analysis = aiData.trap_analysis; fieldsUpdated++ }
        if (fieldsUpdated > 0) {
          form.language = translateTargetLang.value
          alert.success('Translated!', `${fieldsUpdated} field(s) translated to ${targetLangLabel}`)
        } else {
          alert.warning('Partial Success', 'AI responded but could not extract translated data.')
        }
        showTranslateOptions.value = false
      } catch (error) {
        console.error('AI translation failed:', error)
        alert.error('Error', 'Failed to translate question with AI.')
      } finally {
        aiTranslating.value = false
      }
    }

    // Fetch translation when language changes in edit mode
    const onLanguageChange = async () => {
      if (!isEditing.value || !editingId.value) return
      loadingTranslation.value = true
      try {
        const res = await api.get('/v1/admin/translations', {
          params: { content_type: 'question', content_id: editingId.value, language_code: form.language }
        })
        const translations = res.data?.translations || []
        if (translations.length > 0) {
          let td = translations[0].translated_data
          if (typeof td === 'string') { try { td = JSON.parse(td) } catch (e) { td = {} } }

          // Parse nested JSON strings within translated_data
          let opts = td.options
          if (typeof opts === 'string') { try { opts = JSON.parse(opts) } catch (e) { opts = {} } }
          let meta = td.answerMeta
          if (typeof meta === 'string') { try { meta = JSON.parse(meta) } catch (e) { meta = {} } }

          if (td.questionText) form.content = td.questionText
          if (opts) {
            if (opts.a) form.option_a = opts.a
            if (opts.b) form.option_b = opts.b
            if (opts.c) form.option_c = opts.c
            if (opts.d) form.option_d = opts.d
          }
          if (meta) {
            if (meta.correct_answer) form.correct_answer = meta.correct_answer
            if (meta.explanation) form.explanation = meta.explanation
            if (meta.shortcut_method) form.shortcut_method = meta.shortcut_method
            if (meta.memory_hack) form.memory_hack = meta.memory_hack
            if (meta.trap_analysis) form.trap_analysis = meta.trap_analysis
            if (meta.marks) form.marks = meta.marks
          }
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
      } finally {
        loadingTranslation.value = false
      }
    }

    // Helpers
    const formatType = (type) => {
      if (!type) return '-'
      const types = { mcq: 'MCQ', numerical: 'Numerical', match: 'Match', subjective: 'Subjective' }
      return types[type.toLowerCase()] || type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const getTypeBadge = (type) => {
      if (!type) return 'bg-secondary'
      const badges = { mcq: 'bg-primary', numerical: 'bg-info', match: 'bg-warning', subjective: 'bg-secondary' }
      return badges[type.toLowerCase()] || 'bg-secondary'
    }

    const getDifficultyBadge = (difficulty) => {
      const badges = { easy: 'bg-success', medium: 'bg-warning', hard: 'bg-danger' }
      return badges[difficulty] || 'bg-secondary'
    }

    onMounted(fetchInitialData)

    return {
      breadcrumbs, columns, questions, loading, totalCount, currentPage, pageSize, hasMore,
      totalPages, startIndex, endIndex, visiblePages,
      exams, subjects, topics, lessons, tests,
      loadingExams, loadingSubjects, loadingTopics, loadingLessons, loadingTests,
      selectedExam, selectedSubject, selectedTopic, filteredSubjects, filteredTopics,
      filterType, filterDifficulty, filterLanguage, emptyDescription,
      selectedExamName, selectedSubjectName, formLessons, formTests,
      canModify,
      showModal, showViewModal, isEditing, saving, viewingQuestion, aiEnhancing, form,
      aiTranslating, showTranslateOptions, translateTargetLang,
      loadingTranslation, onLanguageChange,
      newAttachmentUrl, attachmentError, addAttachmentUrl, removeAttachmentUrl, getAttachmentFilename, isImageUrl,
      onExamChange, onSubjectChange, goToPage, onPageSizeChange, fetchQuestions,
      openCreateModal, openViewModal, openEditModal, saveQuestion, confirmDelete,
      aiEnhanceQuestion, aiTranslateQuestion, confirmTranslateQuestion, formatType, getTypeBadge, getDifficultyBadge,
      languageOptions
    }
  }
}
</script>

<style scoped>
/* Filter Cascade Section */
.filter-cascade-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #bae6fd;
}

.filter-cascade-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  color: #0369a1;
}

.filter-cascade-header h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.filter-cascade-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.filter-step {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  border: 2px solid #e0e7ff;
  transition: all 0.3s ease;
  opacity: 0.6;
}

.filter-step.active {
  opacity: 1;
  border-color: #6366f1;
}

.filter-step.completed {
  border-color: #10b981;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
}

.filter-step.disabled {
  pointer-events: none;
  opacity: 0.4;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.filter-step.completed .step-number {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.step-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.optional-tag {
  font-weight: 400;
  color: #94a3b8;
  text-transform: none;
}

.filter-select {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.filter-select:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

/* Select Prompt */
.select-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: #f9fafb;
  border-radius: 16px;
  border: 2px dashed #d1d5db;
}

.select-prompt-icon {
  color: #9ca3af;
  margin-bottom: 1.5rem;
}

.select-prompt h3 {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  color: #374151;
}

.select-prompt p {
  margin: 0;
  color: #6b7280;
  max-width: 400px;
}

/* Questions Content */
.questions-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Modal Form */
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
  display: flex;
  align-items: center;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-control:disabled {
  background: #f3f4f6;
  cursor: not-allowed;
}

/* Assignment Section */
.assignment-section {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid #bae6fd;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.8rem;
  color: #0369a1;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.assignment-info {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.assignment-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: white;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #0369a1;
  border: 1px solid #bae6fd;
}

/* AI Enhance Section */
.ai-enhance-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 10px;
  border: 1px solid #fbbf24;
  flex-wrap: wrap;
}

.ai-enhance-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-enhance-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.ai-enhance-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-translate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

.ai-translate-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-radius: 10px;
  border: 1px solid #7dd3fc;
}

.ai-hint {
  font-size: 0.8rem;
  color: #92400e;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Options Section */
.options-section {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.option-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.option-label {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.option-input .form-control {
  flex: 1;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.bg-primary { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); color: white; }
.bg-info { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); color: white; }
.bg-warning { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white; }
.bg-secondary { background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); color: white; }
.bg-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; }
.bg-danger { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); color: white; }
.bg-purple { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); color: white; }

/* Dark Mode */
:global(body.dark-mode) .filter-cascade-section {
  background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%);
  border-color: #0369a1;
}

:global(body.dark-mode) .filter-cascade-header {
  color: #7dd3fc;
}

:global(body.dark-mode) .filter-step {
  background: #1e293b;
  border-color: #334155;
}

:global(body.dark-mode) .filter-step.completed {
  background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
  border-color: #059669;
}

:global(body.dark-mode) .step-label {
  color: #94a3b8;
}

:global(body.dark-mode) .filter-select {
  background: #0f172a;
  border-color: #374151;
  color: #f3f4f6;
}

:global(body.dark-mode) .select-prompt {
  background: #0f172a;
  border-color: #334155;
}

:global(body.dark-mode) .select-prompt h3 {
  color: #f3f4f6;
}

:global(body.dark-mode) .select-prompt p {
  color: #9ca3af;
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}

:global(body.dark-mode) .assignment-section {
  background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%);
  border-color: #0369a1;
}

:global(body.dark-mode) .section-label {
  color: #7dd3fc;
}

:global(body.dark-mode) .assignment-tag {
  background: #1e293b;
  border-color: #0369a1;
  color: #7dd3fc;
}

:global(body.dark-mode) .ai-enhance-section {
  background: linear-gradient(135deg, #78350f 0%, #92400e 100%);
  border-color: #b45309;
}

:global(body.dark-mode) .ai-translate-section {
  background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%);
  border-color: #0369a1;
}

:global(body.dark-mode) .ai-hint {
  color: #fde68a;
}

:global(body.dark-mode) .options-section {
  background: #0f172a;
  border-color: #334155;
}

/* Server-Side Pagination */
.server-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
  border-top: 1px solid #e5e7eb;
  margin-top: -1px;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 8px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-pages {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0 0.5rem;
}

.pagination-page {
  min-width: 36px;
  height: 36px;
  padding: 0 0.5rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 8px;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-page:hover:not(:disabled):not(.active) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-page.active {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-color: #6366f1;
  color: white;
}

.pagination-page.ellipsis {
  border: none;
  background: transparent;
  cursor: default;
}

.pagination-size {
  display: flex;
  align-items: center;
}

.page-size-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
}

.page-size-select:focus {
  outline: none;
  border-color: #6366f1;
}

:global(body.dark-mode) .server-pagination {
  background: #0f172a;
  border-color: #334155;
}

:global(body.dark-mode) .pagination-info {
  color: #9ca3af;
}

:global(body.dark-mode) .pagination-btn {
  background: #1e293b;
  border-color: #374151;
  color: #d1d5db;
}

:global(body.dark-mode) .pagination-btn:hover:not(:disabled) {
  background: #334155;
}

:global(body.dark-mode) .pagination-page {
  background: #1e293b;
  border-color: #374151;
  color: #d1d5db;
}

:global(body.dark-mode) .pagination-page:hover:not(:disabled):not(.active) {
  background: #334155;
}

:global(body.dark-mode) .page-size-select {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}

/* Attachments */
.attachment-input-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.attachment-input-row .form-control {
  flex: 1;
}

.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.75rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s;
}

.attachment-item:hover {
  border-color: #d1d5db;
}

.attachment-thumb {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.attachment-file-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.attachment-name {
  flex: 1;
  font-size: 0.8125rem;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-remove {
  background: none;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #9ca3af;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.attachment-remove:hover {
  color: #ef4444;
  background: #fef2f2;
}

:global(body.dark-mode) .attachment-item {
  background: #1e293b;
  border-color: #374151;
}

:global(body.dark-mode) .attachment-name {
  color: #d1d5db;
}

:global(body.dark-mode) .attachment-remove:hover {
  background: #451a1a;
}
</style>
